import { useEffect, useState } from 'react'

import { Banner, Button, Text } from '@loadsmart/miranda-react'
import { useSettings } from '@loadsmart/react-frontend-settings'

import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import { useSchedules } from './hooks/useScheduler'

import EmailSchedulerComponent from './components/scheduler/EmailScheduler'
import SchedulerComponent from './components/scheduler/SchedulerForm'
import { StyledReportWrapper } from './ManagedAnalyticsPage.styles'

enum DialogFeedbackType {
  NONE = '',
  SUCCESS = 'success',
  FAILED = 'failed',
}

const SchedulePage = () => {
  const { isLoading, values: [isSchedulerEnabled] } = useSettings(["flags.SCHEDULER_ENABLED"])
  const [showEmailScheduler, setShowEmailScheduler] = useState(true)
  const [showCreateSchedulerModal, setShowCreateSchedulerModal] = useState(false)
  const [createSuccessMessage, setCreateSuccessMessage] = useState<string | null>(null)
  const [showDialogFeedback, setShowDialogFeedback] = useState(DialogFeedbackType.NONE)
  const { getSchedules, schedules } = useSchedules()
  const { track } = useAnalytics()

  useEffect(() => {
    getSchedules()
  }, [getSchedules])

  const handleDismiss = () => {
    setShowDialogFeedback(DialogFeedbackType.NONE)
  }

  const handleScheduleNew = async () => {
    await getSchedules()
    
    // Track analytics event for opening scheduler form
    track(AnalyticsEvent.OpenSchedulerForm, {
      'Has Existing Schedules': schedules && schedules.length > 0,
      'Existing Schedules Count': schedules?.length || 0,
      'Is First Schedule': !schedules || schedules.length === 0,
    })
    
    setTimeout(() => {
      setShowCreateSchedulerModal(true)
    }, 200)
  }

  if (isLoading) return null

  if (!isSchedulerEnabled) {
    return (
      <AppLayout>
        <Header
          title={
            <Text color="color-text-primary" variant="body-md-bold">
              Scheduler
            </Text>
          }
        />
        <StyledReportWrapper id="ma-content-root">
          <div style={{ textAlign: 'left' }}>
            <Banner variant="warning">
              <Banner.Title>Feature unavailable</Banner.Title>
              <Banner.Description>The scheduler is currently disabled.</Banner.Description>
            </Banner>
          </div>
        </StyledReportWrapper>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <Header
        title={
          <Text color="color-text-primary" variant="body-md-bold">
            Scheduler
          </Text>
        }
        actions={
          <Button
            variant="primary"
            size="small"
            onClick={handleScheduleNew}
            leading={<span>+</span>}
            style={{ marginRight: 12, height: 30 }}
          >
            Schedule email
          </Button>
        }
      />

      <StyledReportWrapper id="ma-content-root">
        {showDialogFeedback === DialogFeedbackType.SUCCESS && (
          <div style={{ textAlign: 'left' }}>
            <Banner variant="success" onDismiss={handleDismiss} dismissible>
              <Banner.Title>Success</Banner.Title>
              <Banner.Description>Your request has been sent. Soon we'll get in touch.</Banner.Description>
            </Banner>
          </div>
        )}

        {showDialogFeedback === DialogFeedbackType.FAILED && (
          <div style={{ textAlign: 'left' }}>
            <Banner variant="danger" onDismiss={handleDismiss} dismissible>
              <Banner.Title>Your request was not sent.</Banner.Title>
              <Banner.Description>Please contact Loadsmart team to handle the request.</Banner.Description>
            </Banner>
          </div>
        )}

        {showEmailScheduler && (
          <EmailSchedulerComponent
            onScheduleNew={handleScheduleNew}
            successMessage={createSuccessMessage}
            onClearSuccessMessage={() => setCreateSuccessMessage(null)}
          />
        )}

        {showCreateSchedulerModal && (
          <SchedulerComponent
            existingSchedulesCount={schedules?.length || 0}
            onSubmitComplete={() => {
              setShowCreateSchedulerModal(false)
              setShowEmailScheduler(true)
              setCreateSuccessMessage('Schedule created successfully.')
            }}
            onCancel={() => {
              setShowCreateSchedulerModal(false)
              setShowEmailScheduler(true)
            }}
            onBlockedClose={() => {
              setShowCreateSchedulerModal(false)
              setShowEmailScheduler(true)
              // No success message for blocked schedules
            }}
          />
        )}
      </StyledReportWrapper>
    </AppLayout>
  )
}

export default SchedulePage
